/**
 * /app/{tenantSlug}/loans/{loanId} — Loan detail (migration 064).
 *
 * Header (principal / outstanding / status), the installment schedule
 * table, the append-only event ledger, and the finance action buttons
 * (approve / reject / disburse / prepay / write-off). Disburse + write-off
 * are STRICT step-up-TOTP server-side — the client.ts 401 totp_required
 * short-circuit drives the TOTP modal + retry transparently, so no special
 * handling is needed here beyond surfacing the eventual error/success.
 *
 * §17(2): the detail surfaces the interest-free-over-₹20k perquisite
 * disclaimer (the backend computes the flag; v1 does not value it).
 *
 * Surface + Back (QA re-test 2026-10, lib/loan-nav.ts): the borrower is on
 * Me; HR who came from Run → Loans (?from=manage) stays on Run, with Back
 * to that list; any other finance viewer is on the Inbox.
 */

import { useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { z } from "zod";
import { ArrowLeft, BookX, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { useAuth, useFeatures } from "@/lib/auth";
import { isAccountingAdmin } from "@/lib/nav";
import { loanDetailNav } from "@/lib/loan-nav";
import {
  useLoan,
  useApproveLoan,
  useRejectLoan,
  useCancelLoan,
  useDisburseLoan,
  usePrepayLoan,
  useSelfApproveLoan,
  useSelfDisburseLoan,
  useWriteOffLoan,
  usePostLoanDisbursement,
} from "@/hooks/use-loans";
import { formatPaise, rupeesToPaise } from "@/lib/expense-format";
import {
  disbursementDay,
  disbursementMissingFromBooks,
  unbookedCatchUpNotes,
} from "@/lib/loan-books";
import { monthsLabel } from "@/lib/approval-copy";
import { installmentStatusLabel } from "@/lib/loan-schedule";
import { MeModuleShell } from "@/components/shared/me-module-shell";
import { PageHeader } from "@/components/shared/page-header";
import { ModuleNav } from "@/components/shared/module-nav";
import { InboxPage } from "@/components/inbox/inbox-page";
import { RequestSurfaceClaim } from "@/components/dashboard/surface-claim";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDuePeriod } from "./app.$tenantSlug.loans.index";

// ?from=me|manage|inbox — where the loan was opened from (lib/loan-nav.ts).
const loanDetailSearchSchema = z.object({
  from: z.enum(["me", "manage", "inbox"]).optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/loans/$loanId",
)({
  validateSearch: loanDetailSearchSchema,
  component: LoanDetailRoute,
});

/** Manage → Payroll → Loans & Advances header + the Payroll strip. */
function RunLoanShell({
  title,
  actions,
  children,
}: Readonly<{ title: string; actions?: ReactNode; children: ReactNode }>) {
  return (
    <>
      <PageHeader title={title} actions={actions} />
      <ModuleNav />
      {children}
    </>
  );
}

function LoanDetailRoute() {
  const { tenantSlug, loanId } = useParams({ strict: false }) as {
    tenantSlug: string;
    loanId: string;
  };
  const id = Number(loanId);
  const { from } = Route.useSearch();
  const { user } = useAuth();
  const { has: hasFeature } = useFeatures();
  const finance = isAccountingAdmin(user?.role);

  const { data, isLoading } = useLoan(id);
  const approve = useApproveLoan();
  const reject = useRejectLoan();
  const cancel = useCancelLoan();
  const disburse = useDisburseLoan();
  const prepay = usePrepayLoan();
  const writeOff = useWriteOffLoan();
  const postBooks = usePostLoanDisbursement();
  const selfApprove = useSelfApproveLoan();
  const selfDisburse = useSelfDisburseLoan();

  const [rejectReason, setRejectReason] = useState("");
  const [prepayStr, setPrepayStr] = useState("");
  const [writeOffNote, setWriteOffNote] = useState("");

  if (isLoading || !data) {
    const LoadingShell = from === "manage" ? RunLoanShell : MeModuleShell;
    return (
      <LoadingShell title="Loan">
        <div className="space-y-4">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </LoadingShell>
    );
  }
  const { loan, installments, events, perquisite_disclaimer } = data;
  // A closed / written-off loan's untouched EMIs still carry "scheduled";
  // the row label comes from the loan status + its ledger (FE5).
  const eventKinds = (events ?? []).map((e) => e.kind);

  async function run(p: Promise<unknown>, ok: string) {
    try {
      await p;
      toast.success(ok);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  const isOwner = user?.user_id === loan.employee_id;
  // Nobody decides their own loan (server: 403 self_decision) — another
  // finance user must approve / disburse / prepay / write it off. The one
  // exception: the workspace's ONLY finance user may approve and disburse
  // their own loan explicitly (server-computed, audited — 2026-10 QA #4).
  const canDecide = finance && !isOwner;
  const canSelfApprove = isOwner && data.can_self_approve === true;
  const canSelfDisburse = isOwner && data.can_self_disburse === true;
  // …and book it when it was disbursed while the books were off.
  const canSelfPostBooks = isOwner && data.can_self_post_books === true;
  const awaitsOtherFinance =
    finance &&
    isOwner &&
    !canSelfApprove &&
    !canSelfDisburse &&
    (loan.status === "requested" ||
      loan.status === "approved" ||
      loan.status === "disbursed");
  // The borrower is on Me; HR from Run → Loans on Run; any other finance
  // user reviewing someone else's loan on the Inbox (lib/loan-nav.ts).
  const nav = loanDetailNav({ isOwner, isFinance: finance, from });
  const Shell =
    nav.surface === "me" ? MeModuleShell : nav.surface === "run" ? RunLoanShell : InboxPage;

  return (
    <Shell
      title={`Loan #${loan.id}`}
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to={nav.back} params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <RequestSurfaceClaim ownerId={loan.employee_id} surface={nav.surface} />
      <div className="space-y-6">
      {perquisite_disclaimer && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            This interest-free loan exceeds ₹20,000 and may attract a §17(2)
            perquisite (imputed at SBI rates). Informational only — the
            platform does not value the perquisite in v1.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="flex flex-wrap items-center gap-8 pt-6">
          <Stat label="Employee" value={loan.employee_name || `#${loan.employee_id}`} />
          <Stat label="Kind" value={loan.kind} />
          <Stat label="Principal" value={formatPaise(loan.principal_paise)} />
          <Stat label="Outstanding" value={formatPaise(loan.outstanding_paise)} />
          <Stat label="Tenure" value={monthsLabel(loan.tenure_months)} />
          <div>
            <div className="text-xs uppercase text-muted-foreground">Status</div>
            <StatusBadge status={loan.status} />
          </div>
        </CardContent>
      </Card>

      {/* Books catch-up (QA books #14): disbursed while Accounting was off.
          The workspace's only finance user may book their own loan too
          (explicit, audited — like self-approve / self-disburse). */}
      {(canDecide || canSelfPostBooks) &&
        hasFeature("accounting") &&
        loan.disbursed_at &&
        disbursementMissingFromBooks(loan) && (
          <PostToBooksAlert
            principalPaise={loan.principal_paise}
            disbursedOn={disbursementDay(loan.disbursed_at)}
            unbookedNotes={unbookedCatchUpNotes(
              data.unbooked_backlog_paise ?? 0,
              data.unbooked_manual_paise ?? 0,
              formatPaise,
            )}
            pending={postBooks.isPending}
            onConfirm={async (date) => {
              // Kept open on a refusal (e.g. a locked period) so another
              // date can be picked.
              try {
                await postBooks.mutateAsync({ id: loan.id, date, selfPost: isOwner });
                toast.success("Disbursement posted to the books");
                return true;
              } catch (err) {
                toast.error(toApiError(err).message);
                return false;
              }
            }}
          />
        )}

      {/* Actions */}
      {awaitsOtherFinance && (
        <p className="text-sm text-muted-foreground">
          This is your own loan — another finance user has to approve,
          disburse or close it.
        </p>
      )}
      {canSelfApprove && loan.status === "requested" && (
        <SelfLoanAction
          verb="approve"
          consequence="You can then disburse it here."
          pending={selfApprove.isPending}
          onConfirm={() =>
            void run(selfApprove.mutateAsync(loan.id), "Approved — recorded as a self-approval.")
          }
        />
      )}
      {canSelfDisburse && loan.status === "approved" && (
        <SelfLoanAction
          verb="disburse"
          consequence="Disbursing posts the loan to the books and starts the EMI schedule — the ledger entry can't be undone. Requires step-up TOTP."
          pending={selfDisburse.isPending}
          onConfirm={() =>
            void run(selfDisburse.mutateAsync(loan.id), "Disbursed — recorded as a self-approval.")
          }
        />
      )}
      <div className="flex flex-wrap gap-2">
        {isOwner && loan.status === "requested" && (
          <Button
            variant="outline"
            disabled={cancel.isPending}
            onClick={() => run(cancel.mutateAsync(loan.id), "Loan cancelled")}
          >
            Cancel Request
          </Button>
        )}
        {canDecide && loan.status === "requested" && (
          <>
            <Button
              disabled={approve.isPending}
              onClick={() => run(approve.mutateAsync(loan.id), "Loan approved")}
            >
              Approve
            </Button>
            <RejectDialog
              pending={reject.isPending}
              reason={rejectReason}
              setReason={setRejectReason}
              onConfirm={() =>
                run(
                  reject.mutateAsync({ id: loan.id, reason: rejectReason.trim() }),
                  "Loan rejected",
                )
              }
            />
          </>
        )}
        {canDecide && loan.status === "approved" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={disburse.isPending} className="gap-1.5">
                {disburse.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Disburse (TOTP)
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Disburse this loan?</AlertDialogTitle>
                <AlertDialogDescription>
                  Disbursing posts the loan to the books and starts the EMI
                  schedule — the ledger entry can&apos;t be undone. Requires
                  step-up TOTP.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={disburse.isPending}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={disburse.isPending}
                  className="gap-1.5"
                  onClick={(e) => {
                    e.preventDefault();
                    void run(
                      disburse.mutateAsync(loan.id),
                      "Loan disbursed + posted to books",
                    );
                  }}
                >
                  {disburse.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  Disburse
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {canDecide && loan.status === "disbursed" && (
          <>
            <PrepayDialog
              pending={prepay.isPending}
              amount={prepayStr}
              setAmount={setPrepayStr}
              onConfirm={() => {
                const paise = rupeesToPaise(prepayStr);
                if (paise == null || paise <= 0) {
                  toast.error("Enter a valid prepayment amount");
                  return;
                }
                void run(
                  prepay.mutateAsync({ id: loan.id, amountPaise: paise }),
                  "Prepayment recorded + posted",
                );
              }}
            />
            <WriteOffDialog
              pending={writeOff.isPending}
              note={writeOffNote}
              setNote={setWriteOffNote}
              onConfirm={() =>
                run(
                  writeOff.mutateAsync({ id: loan.id, note: writeOffNote }),
                  "Loan written off + posted",
                )
              }
            />
          </>
        )}
      </div>

      {/* Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">EMI Schedule</CardTitle>
        </CardHeader>
        <CardContent>
          {installments.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No schedule yet — generated on disbursement.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Outstanding After</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {installments.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-mono">{i.seq_no}</TableCell>
                    <TableCell>{formatDuePeriod(i.due_year, i.due_month)}</TableCell>
                    <TableCell className="text-right">
                      {formatPaise(i.amount_paise)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatPaise(i.outstanding_after_paise)}
                    </TableCell>
                    <TableCell>{installmentStatusLabel(i.status, loan.status, eventKinds)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Event ledger */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ledger</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {events.map((e) => (
                <li
                  key={e.id}
                  className="flex justify-between border-b py-1 last:border-0"
                >
                  <span className="capitalize text-muted-foreground">
                    {e.kind.replace("_", " ")}
                    {e.note ? ` — ${e.note}` : ""}
                  </span>
                  <span className="font-medium">
                    {formatPaise(e.amount_paise)} · bal{" "}
                    {formatPaise(e.outstanding_after_paise)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      </div>
    </Shell>
  );
}

/**
 * The sole finance user's explicit self-action on their OWN loan
 * (approve a request / disburse an approved loan) — a confirm dialog that
 * says it is recorded as a self-approval (2026-10 QA #4).
 */
function SelfLoanAction({
  verb,
  consequence,
  pending,
  onConfirm,
}: Readonly<{
  verb: "approve" | "disburse";
  consequence: string;
  pending: boolean;
  onConfirm: () => void;
}>) {
  const label = verb === "approve" ? "Approve my own loan" : "Disburse my own loan (TOTP)";
  return (
    <div className="w-full rounded-md border border-dashed border-input p-3">
      <p className="mb-2 text-sm text-muted-foreground">
        You&apos;re the only finance user in this workspace, so nobody else can{" "}
        {verb} this loan. You can {verb} it yourself — it&apos;s recorded in
        the audit log as a self-approval. Add another admin or an accountant
        to avoid this.
      </p>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" disabled={pending} className="gap-1.5">
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {label}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {verb === "approve" ? "Approve your own loan?" : "Disburse your own loan?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Nobody else in this workspace can {verb} it. This is recorded in
              the audit log as a self-approval. {consequence}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Not Now</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending}
              className="gap-1.5"
              onClick={(e) => {
                e.preventDefault();
                onConfirm();
              }}
            >
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {verb === "approve" ? "Approve" : "Disburse"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold capitalize">{value}</div>
    </div>
  );
}

function RejectDialog({
  pending,
  reason,
  setReason,
  onConfirm,
}: {
  pending: boolean;
  reason: string;
  setReason: (s: string) => void;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="outline">Reject</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reject this loan request?</AlertDialogTitle>
          <AlertDialogDescription>
            Rejecting closes the loan request for good.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reject-reason">Reason (Required)</Label>
          <Textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending || reason.trim() === ""}
            className={cn(buttonVariants({ variant: "destructive" }))}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
              setOpen(false);
            }}
          >
            Reject
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PrepayDialog({
  pending,
  amount,
  setAmount,
  onConfirm,
}: {
  pending: boolean;
  amount: string;
  setAmount: (s: string) => void;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Record Prepayment</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record Prepayment</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="prepay-amount">Amount (₹)</Label>
          <Input
            id="prepay-amount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Posts DR Bank / CR Employee Loan Receivable and re-amortises the
            remaining schedule (tenure shortens).
          </p>
        </div>
        <DialogFooter>
          <Button
            disabled={pending}
            onClick={() => {
              onConfirm();
              setOpen(false);
            }}
          >
            Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WriteOffDialog({
  pending,
  note,
  setNote,
  onConfirm,
}: {
  pending: boolean;
  note: string;
  setNote: (s: string) => void;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive">Write Off (TOTP)</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Write off remaining balance?</AlertDialogTitle>
          <AlertDialogDescription>
            Writing off posts the outstanding balance to expenses and clears
            the receivable — it&apos;s irreversible. Requires step-up TOTP.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="writeoff-note">Note (Optional)</Label>
          <Textarea
            id="writeoff-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Posts DR Office Expenses / CR Employee Loan Receivable for the
            outstanding balance.
          </p>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={cn(buttonVariants({ variant: "destructive" }))}
            disabled={pending}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
              setOpen(false);
            }}
          >
            Write Off
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Books catch-up for a loan disbursed while the Accounting module was off
 *  (2026-10 browser QA, books #14). Posting a journal is permanent, so the
 *  confirm is an AlertDialog. */
function PostToBooksAlert({
  principalPaise,
  disbursedOn,
  unbookedNotes,
  pending,
  onConfirm,
}: {
  principalPaise: number;
  disbursedOn: string;
  /** Server-computed (audit Q16): what stays out of 1210 and how it gets in. */
  unbookedNotes: string[];
  pending: boolean;
  onConfirm: (date: string) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(disbursedOn);
  return (
    <Alert>
      <BookX className="h-4 w-4" />
      <AlertTitle>This disbursement is not in the books</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>
          The loan was disbursed while the Accounting module was off, so 1210
          Employee Loan Receivable never got the {formatPaise(principalPaise)}
          {" "}debit. EMI recoveries booked since then leave 1210 in credit.
        </p>
        <AlertDialog
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (o) setDate(disbursedOn);
          }}
        >
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={pending}>
              Post Disbursement to Books
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Post the disbursement to the books?</AlertDialogTitle>
              <AlertDialogDescription>
                Posts DR 1210 Employee Loan Receivable / CR 1110 Bank for{" "}
                {formatPaise(principalPaise)}. A posted journal can&apos;t be
                deleted — only reversed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              <Label htmlFor="post-books-date">Posting Date</Label>
              <Input
                id="post-books-date"
                type="date"
                value={date}
                min={disbursedOn}
                onChange={(e) => setDate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Defaults to the disbursement day. If that month is locked in
                the books, pick a date in an open period.
              </p>
              {unbookedNotes.length > 0 && (
                <div className="space-y-1 text-xs text-muted-foreground" data-testid="loan-books-unbooked">
                  {unbookedNotes.map((note) => (
                    <p key={note}>{note}</p>
                  ))}
                </div>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={pending || date === ""}
                className="gap-1.5"
                onClick={(e) => {
                  e.preventDefault();
                  void onConfirm(date).then((ok) => {
                    if (ok) setOpen(false);
                  });
                }}
              >
                {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                Post to Books
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AlertDescription>
    </Alert>
  );
}
